// Shared pieces for public pages that exist in English and Spanish at their own
// addresses (/blog/… and /es/blog/…, /tools/… and /es/tools/…).
import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandHeader } from './Brand';
import SiteFooter from './SiteFooter';
import { useI18n } from './i18n';
import { trackClick } from './analytics';

// "/blog/x" in English, "/es/blog/x" in Spanish.
export function localPath(lang, path) {
  return `${lang === 'es' ? '/es' : ''}${path}`;
}

// The URL picks the language. A Spanish visitor arriving on an English address is
// sent to the Spanish one, and switching language later moves to the matching address.
// `path` is the page's address without the /es prefix.
export function usePageLanguage(forceLang, path) {
  const { lang, setLang } = useI18n();
  const navigate = useNavigate();
  const pageLang = forceLang || 'en';
  const settled = useRef(false);
  // Remember the Spanish address as the visitor's choice, like the /es homepage.
  useEffect(() => {
    if (forceLang) setLang(forceLang);
  }, [forceLang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lang === pageLang) {
      settled.current = true;
    } else if (!settled.current && forceLang) {
      // Waiting for the language set above.
    } else {
      navigate(localPath(lang, path), { replace: true });
    }
  }, [lang, pageLang]); // eslint-disable-line react-hooks/exhaustive-deps
  return pageLang;
}

export function useTitle(title) {
  useEffect(() => {
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);
}

// Header with log in / sign up (or a link back to the journal), the page, and the site footer.
export function PublicShell({ user, className = '', children }) {
  const { t, lang } = useI18n();
  return (
    <div className={`App ${className}`}>
      <BrandHeader>
        <Link to={lang === 'es' ? '/es/pricing' : '/pricing'} className="header-link">{t('Pricing')}</Link>
        {user ? (
          <Link to="/app" className="header-link">{t('Journal')}</Link>
        ) : (
          <>
            <Link to="/login" className="header-link">{t('Log in')}</Link>
            <Link to="/signup" className="nav-button-small" onClick={() => trackClick('header-signup')}>{t('Sign up free')}</Link>
          </>
        )}
      </BrandHeader>
      {children}
      <SiteFooter />
    </div>
  );
}

// Any address that isn't a page: say so, and offer the main places to go.
export function NotFoundPage({ user }) {
  const { t } = useI18n();
  useTitle(t('Page not found | Market Matrix'));
  return (
    <PublicShell user={user} className="not-found-page">
      <div className="page-title">
        <p className="eyebrow">404</p>
        <h1>{t('Page not found')}</h1>
        <p className="subtitle">{t("The link may be old or mistyped. Here's where you can go instead.")}</p>
      </div>
      <ul className="not-found-links">
        <li><Link to={user ? '/app/home' : '/'} className="cta-button">{user ? t('Open your journal') : t('Go to the homepage')} &rarr;</Link></li>
        <li><Link to="/blog">{t('Blog')}</Link></li>
        <li><Link to="/learn">{t('Start here: learn to trade')}</Link></li>
        <li><Link to="/tools">{t('Free calculators')}</Link></li>
        <li><Link to="/contact">{t('Help & contact')}</Link></li>
      </ul>
    </PublicShell>
  );
}
